import React from 'react';
import { Link } from 'react-router-dom';
import { locations } from '@/data/mock';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Phone, ArrowRight, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

const highlightLocations = locations.slice(0, 3);

const LocationsSection = ({ className }) => {
  return (
    <section className={cn('py-20 bg-slate-950 text-white', className)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <Badge className="bg-white/10 text-white uppercase tracking-widest">
              National Delivery Network
            </Badge>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold leading-tight">
              Regional hubs positioned where infrastructure programs advance the fastest.
            </h2>
            <p className="mt-4 text-slate-300 text-lg">
              From Stamford to Dallas and Los Angeles, each studio combines embedded local insight with
              Redspire’s national technical leadership. Dedicated teams manage complex programs end-to-end,
              coordinating design technology, construction oversight, and community engagement.
            </p>
          </div>
          <Link to="/locations">
            <Button variant="secondary" className="gap-2">
              Explore All Locations
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {highlightLocations.map((location) => (
            <Card key={location.id} className="bg-white text-gray-900 border border-white/10 shadow-2xl">
              <div className="h-48 w-full overflow-hidden">
                <img
                  src={location.image}
                  alt={location.name}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <CardContent className="p-6 space-y-4">
                <div>
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="text-xs uppercase tracking-wide">
                      {location.city}
                    </Badge>
                    <span className="text-sm font-semibold text-red-600">{location.leadership}</span>
                  </div>
                  <h3 className="mt-3 text-xl font-semibold">{location.name}</h3>
                </div>
                <p className="text-sm text-gray-600">{location.regionalFocus}</p>
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-red-600" />
                    <span>{location.address}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-red-600" />
                    <a href={`tel:${location.phone}`} className="hover:text-red-600">
                      {location.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-red-600" />
                    <span>{location.hours}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  {location.services.slice(0, 3).map((service) => (
                    <Badge key={service} variant="outline" className="text-xs">
                      {service}
                    </Badge>
                  ))}
                </div>
                <div className="pt-4">
                  <Button asChild variant="outline" className="w-full gap-2">
                    <a href={location.mapUrl} target="_blank" rel="noopener noreferrer">
                      View in Maps
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LocationsSection;

